import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { isDesktop } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import MarkitHeaderLogo from "../../components/MarkitHeaderLogo";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { BackButton } from "../../components/Buttons/BackButton";
import PopupModalMessageTag from "../../components/Containers/PopupModalMessageTag";
import { NavigationId } from "../../navigation/AppParamList";
import { addAlpha, Colors } from "../../utils/colors";
import { MARKIT_DOCS, MARKITAI } from "@markit/common.utils";
import { MAX_FACEBOOK_PAGES } from "../../components/CreatorDashboard/AutoDM/Facebook/FacebookPanelSection";
import { SocialPlatformIconItem } from "../../components/DisplayItem/AutoDM/SocialPlatformIconItem";
import {
  connectFacebookPage,
  listFacebookPages,
} from "../../utils/facebookApiUtils";
import {
  getAccountSlice,
  loadInitialFacebookPages,
  LoginState,
} from "../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { DMAutomationPlatform, NotificationType } from "@markit/common.types";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import LabelButton from "../../components/Buttons/LabelButton";

type PendingFacebookPage = {
  id: string;
  pageName: string;
  profilePicUrl: string;
  isAdmin: boolean;
};

// Landing page shown after the Facebook OAuth redirect. Facebook's OAuth resolves to a
// list of Pages the creator manages rather than a single account, so this page fetches
// that list and lets them pick which Page to actually connect.
const FacebookConnectionPage = memo(function FacebookConnectionPageFn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accountData, facebookPages, loggedIn, appInitialized } =
    useSelector(getAccountSlice);
  const [searchParams] = useSearchParams();

  const isFailure = searchParams.get("fb") === "failure";
  const isAccountLimit = searchParams.get("fb") === "account_limit";
  const isDeletion = searchParams.get("fb") === "deletion";
  const isPendingPick = !isFailure && !isAccountLimit && !isDeletion;

  const [pendingPages, setPendingPages] = useState<PendingFacebookPage[]>([]);
  const [loadingPages, setLoadingPages] = useState(isPendingPick);
  const [connectingPageId, setConnectingPageId] = useState("");
  const [connectedPageName, setConnectedPageName] = useState("");

  const [isScrollingPageList, setIsScrollingPageList] = useState(false);
  const pageListScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const onPageListScroll = useCallback(() => {
    setIsScrollingPageList(true);
    if (pageListScrollTimeoutRef.current) {
      clearTimeout(pageListScrollTimeoutRef.current);
    }
    pageListScrollTimeoutRef.current = setTimeout(
      () => setIsScrollingPageList(false),
      800
    );
  }, []);

  // Pages already connected to this account (e.g. re-running the OAuth flow to grant
  // access to more Pages) - shown as "Connected" instead of an actionable Connect button.
  const connectedPageIds = useMemo(
    () => new Set(facebookPages.map((page) => page.id)),
    [facebookPages]
  );

  const fetchPendingPages = useCallback(async () => {
    setLoadingPages(true);
    try {
      const pages = await listFacebookPages();
      setPendingPages(pages);
    } catch (err) {
      console.error("Failed to fetch pending Facebook Pages:", err);
    } finally {
      setLoadingPages(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!isPendingPick) return;
    if (loggedIn === LoginState.LOGGED_IN && !appInitialized) return;
    await fetchPendingPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, appInitialized]);

  const connectOnPress = async (page: PendingFacebookPage) => {
    setConnectingPageId(page.id);
    try {
      await connectFacebookPage(page.id);
      await dispatch(loadInitialFacebookPages(accountData.uid));
      setConnectedPageName(page.pageName);
    } catch (err) {
      console.error("Failed to connect Facebook Page:", err);
      showNotificationBanner(
        dispatch,
        "Failed to connect this Page",
        NotificationType.NEGATIVE
      );
    } finally {
      setConnectingPageId("");
    }
  };

  const content = useMemo(
    () =>
      isAccountLimit
        ? {
            header: "Something Failed!",
            title: "Page Limit Reached",
            subtitle: `You can connect up to ${MAX_FACEBOOK_PAGES} Facebook Pages. Please disconnect one before adding another.`,
          }
        : isFailure
          ? {
              header: "Something Failed!",
              title: "Facebook Connection Failed",
              subtitle:
                "We could not connect your Facebook account. Please try again from your integrations page.",
            }
          : isDeletion
            ? {
                header: "Updated",
                title: "Facebook Disconnected",
                subtitle:
                  "We've removed your Facebook link from Markit and deleted the access we had to your Page. Automations that used this channel will no longer run until you connect again.",
              }
            : null,
    [isAccountLimit, isDeletion, isFailure]
  );

  return (
    <div
      style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}
    >
      <Helmet>
        <title>Facebook Connection | Markit AI</title>
        <meta
          name="description"
          content={
            isFailure
              ? "Your Facebook account could not be connected."
              : isDeletion
                ? "Your Facebook account has been disconnected."
                : "Choose which Facebook Page to connect to Markit."
          }
        />
        <link rel="canonical" href={MARKITAI + "/facebook/connection"} />
      </Helmet>
      <div style={{ position: "absolute", top: 24, left: 24 }}>
        <MarkitHeaderLogo fullLogo alternateSize={24} />
      </div>
      <div
        className="ColumnCenter"
        style={{
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className={isDesktop ? "AlignedRow" : "ColumnNormal"}
          style={{
            width: "100%",
            maxWidth: isDesktop ? 920 : 480,
            gap: isDesktop ? 80 : 14,
            alignItems: isDesktop ? "center" : "flex-start",
            justifyContent: "center",
          }}
        >
          {isDesktop ? null : (
            <div style={{ paddingInline: 14 }}>
              <SocialPlatformIconItem
                platform={DMAutomationPlatform.FACEBOOK}
                size={40}
                noBorder
              />
            </div>
          )}

          {content ? (
            // Failure / account-limit / deletion states - static, same shape as InstagramConnectionPage.
            <div
              className="ColumnNormal"
              style={{
                maxWidth: 480,
                gap: isDesktop ? 24 : 14,
                paddingInline: isDesktop ? 0 : 14,
              }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span
                  className="bodyMedium"
                  style={{
                    color:
                      isFailure || isAccountLimit ? Colors.RED3 : Colors.BLUE5,
                  }}
                >
                  {content.header}
                </span>
                <span
                  style={{
                    fontSize: 34,
                    fontWeight: 500,
                    display: "block",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {content.title}
                </span>
                <span className="bodySubtext" style={{ maxWidth: 400 }}>
                  {content.subtitle}
                </span>
              </div>
              <RectangleButton
                buttonLabel="Back to Markit"
                onPress={() => navigate(NavigationId.HOME_AUTO_DM)}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={40}
                altPaddingVert={14}
                containerStyles={{ width: "fit-content" }}
              />
            </div>
          ) : connectedPageName ? (
            // A Page was just connected - same success shape as Instagram's.
            <div
              className="ColumnNormal"
              style={{
                maxWidth: 480,
                gap: isDesktop ? 24 : 14,
                paddingInline: isDesktop ? 0 : 14,
              }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span className="bodyMedium" style={{ color: Colors.BLUE5 }}>
                  Success!
                </span>
                <span style={{ fontSize: 34, fontWeight: 500 }}>
                  {connectedPageName} is connected!
                </span>
                <span className="bodySubtext" style={{ maxWidth: 400 }}>
                  Create your first Facebook automation and try the power of the
                  Facebook Messenger channel.
                </span>
              </div>
              <RectangleButton
                buttonLabel="Let's Start"
                onPress={() => navigate(NavigationId.HOME_AUTO_DM)}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={40}
                altPaddingVert={14}
                containerStyles={{ width: "fit-content" }}
              />
            </div>
          ) : (
            // Page picker - the step Instagram's OAuth never needs.
            <div
              className="ColumnNormal"
              style={{
                maxWidth: 480,
                gap: isDesktop ? 24 : 14,
                paddingInline: isDesktop ? 0 : 14,
                width: "100%",
              }}
            >
              <BackButton onPress={() => navigate(NavigationId.HOME_AUTO_DM)} />
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span className="bodyMedium" style={{ color: Colors.BLUE5 }}>
                  Almost done!
                </span>
                <span style={{ fontSize: 34, fontWeight: 500 }}>
                  Choose a Page to connect
                </span>
                <span className="bodySubtext" style={{ maxWidth: 400 }}>
                  Pick which Facebook Page you&apos;d like to link to Markit.
                  You can connect more later from your Integrations page.
                </span>
              </div>
              {loadingPages ? (
                <span className="bodySubtext">
                  Loading your Pages... This can take up to a minute, please keep
                  this page open.
                </span>
              ) : pendingPages.length === 0 ? (
                <span className="bodySubtext">
                  We couldn&apos;t find any Facebook Pages you manage. Make sure
                  you&apos;re an admin on the Page you want to connect, then try
                  again.
                </span>
              ) : (
                <div
                  className={`ColumnNormal FacebookPageListScroll${
                    isScrollingPageList ? " IsScrolling" : ""
                  }`}
                  onScroll={onPageListScroll}
                  style={{
                    gap: 10,
                    width: "100%",
                    maxHeight: 320,
                    overflowY: "auto",
                  }}
                >
                  <style>
                    {`.FacebookPageListScroll {
                      scrollbar-width: none;
                    }
                    .FacebookPageListScroll::-webkit-scrollbar {
                      width: 6px;
                    }
                    .FacebookPageListScroll::-webkit-scrollbar-thumb {
                      background-color: transparent;
                      border-radius: 3px;
                    }
                    .FacebookPageListScroll.IsScrolling {
                      scrollbar-width: thin;
                      scrollbar-color: #c1c1c1 transparent;
                    }
                    .FacebookPageListScroll.IsScrolling::-webkit-scrollbar-thumb {
                      background-color: #c1c1c1;
                    }`}
                  </style>
                  {pendingPages.map((page) => (
                    <div
                      key={page.id}
                      className="AlignedRowSpaced"
                      style={{
                        gap: 14,
                        border: `0.5px solid ${Colors.GRAY2}`,
                        borderRadius: 12,
                        padding: 14,
                      }}
                    >
                      <div className="AlignedRow" style={{ gap: 12 }}>
                        {page.profilePicUrl ? (
                          <img
                            src={page.profilePicUrl}
                            alt={page.pageName}
                            style={{ width: 40, height: 40, borderRadius: 20 }}
                          />
                        ) : null}
                        <span className="bodyMedium">{page.pageName}</span>
                      </div>
                      {connectedPageIds.has(page.id) ? (
                        <LabelButton
                          text="Connected"
                          color={Colors.GREEN2}
                          backgroundColor={addAlpha(Colors.GREEN2, 0.1)}
                          borderColor={Colors.GREEN2}
                          containerStyles={{ fontSize: 12, fontWeight: 500 }}
                          icon={
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: Colors.GREEN2,
                              }}
                            />
                          }
                        />
                      ) : page.isAdmin ? (
                        <RectangleButton
                          buttonLabel="Connect"
                          onPress={() => connectOnPress(page)}
                          loading={connectingPageId === page.id}
                          altColor={Colors.BLACK}
                          altTextColor={Colors.WHITE}
                          altPaddingHorz={20}
                          altPaddingVert={10}
                          containerStyles={{ width: "fit-content" }}
                        />
                      ) : (
                        <div className="AlignedRow" style={{ gap: 7 }}>
                          <RectangleButton
                            buttonLabel="Connect"
                            onPress={() => {}}
                            disabled
                            altColor={Colors.BLACK1}
                            altTextColor={Colors.WHITE}
                            altPaddingHorz={20}
                            altPaddingVert={10}
                            containerStyles={{ width: "fit-content" }}
                          />
                          <PopupModalMessageTag
                            message=""
                            alternateMessageComp={
                              <Icon
                                icon="mdi:help-circle-outline"
                                height={20}
                                style={{ color: Colors.GRAY1 }}
                              />
                            }
                            popupTitle="Admin access required"
                            popupSubtext="Only Facebook Page Admins can connect Markit to a Page. Please ask the Page Admin to connect this Page."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!loadingPages ? (
                <div className="AlignedRow" style={{ gap: 14 }}>
                  <span
                    className="bodySmallMedium AlignedRowSelect"
                    style={{ color: Colors.BLUE5 }}
                    onClick={fetchPendingPages}
                  >
                    Refresh Page List
                  </span>
                  <span
                    className="bodySmallMedium AlignedRowSelect"
                    style={{ color: Colors.BLUE5 }}
                    onClick={() =>
                      window.open(MARKIT_DOCS + "/connectfacebook", "_blank")
                    }
                  >
                    Need Help?
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {isDesktop ? (
            <SocialPlatformIconItem
              platform={DMAutomationPlatform.FACEBOOK}
              size={150}
              noBorder
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default FacebookConnectionPage;
